import {
  Avatar,
  Box,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconUsers,
} from "@tabler/icons-react";
import { ReactComponent as SearchIcon } from "../assets/search-icon.svg";
import { Button } from "../components";
import { useTheme } from "@mui/material";
import { useState } from "react";
import { formatStat } from "../util/helpers";

const platformToIcon = {
  youtube: <IconBrandYoutube size="20" color="var(--main-text-color)" />,
  tiktok: <IconBrandTiktok size="20" color="var(--main-text-color)" />,
  facebook: <IconBrandFacebook size="20" color="var(--main-text-color)" />,
  instagram: <IconBrandInstagram size="20" color="var(--main-text-color)" />,
};

export default function CampaignTable({ campaigns, tabValue, campaignStatsMap = {} }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  return (
    <Stack direction="column" spacing={2} marginTop={2}>
      <TextField
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Campaigns"
        size="small"
        style={{
          background: "var(--Colors-Background-bg-primary, #FFF)",
          borderRadius: "var(--radius-md, 8px)",
          border: "1px solid var(--Colors-Border-border-primary, #D0D5DD)",
        }}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {campaigns
        ?.filter(
          (campaign) =>
            (campaign.active && tabValue === "active") ||
            (!campaign.active && tabValue === "inactive")
        )
        .filter((campaign) =>
          campaign.name.toLowerCase().includes(search.toLowerCase())
        )
        .map((campaign) => {
          const stats = campaignStatsMap[campaign.id];
          return (
            <Box
              key={campaign.id}
              sx={{
                borderRadius: "16px",
                background: "#fff",
                border: "1px solid var(--neutral-200)",
                p: "20px 24px",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "var(--secondary-200)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  transform: "translateY(-1px)",
                },
              }}
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(200px, 1.5fr) 90px 90px 90px auto",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Campaign info */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar
                    src={campaign.image}
                    sx={{
                      width: 44,
                      height: 44,
                      border: "2px solid var(--neutral-100)",
                      flexShrink: 0,
                    }}
                  />
                  <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: "var(--main-text-color)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {campaign.name}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                      <Chip
                        label={campaign.active ? "Active" : "Inactive"}
                        icon={
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              backgroundColor: campaign.active
                                ? "var(--icon-color-green)"
                                : "var(--icon-color-pink)",
                            }}
                          />
                        }
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: campaign.active
                            ? theme.palette.success.light
                            : theme.palette.error.light,
                          color: campaign.active
                            ? theme.palette.success.main
                            : "white",
                        }}
                      />
                      {campaign.influencers?.length > 0 && (
                        <Chip
                          label={campaign.influencers.length}
                          size="small"
                          icon={<IconUsers size="12" color={theme.palette.info.main} />}
                          sx={{
                            height: 24,
                            fontSize: "0.7rem",
                            backgroundColor: theme.palette.info.light,
                            color: theme.palette.info.main,
                          }}
                        />
                      )}
                      {campaign.platforms?.map((p) => (
                        <Box
                          key={p}
                          sx={{ display: "flex", alignItems: "center", opacity: 0.7 }}
                        >
                          {platformToIcon[p]}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>

                {/* Views */}
                <Stack alignItems="center" spacing={0.25}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--body-text-color-500)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.6rem",
                    }}
                  >
                    Views
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "var(--main-text-color)" }}
                  >
                    {stats ? formatStat(stats.views) : "—"}
                  </Typography>
                </Stack>

                {/* Engagement */}
                <Stack alignItems="center" spacing={0.25}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--body-text-color-500)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.6rem",
                    }}
                  >
                    Engagement
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "var(--main-text-color)" }}
                  >
                    {stats ? formatStat(stats.engagement) : "—"}
                  </Typography>
                </Stack>

                {/* Eng. Rate */}
                <Stack alignItems="center" spacing={0.25}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--body-text-color-500)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.6rem",
                    }}
                  >
                    Eng. Rate
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: "var(--secondary-500)" }}
                  >
                    {stats ? `${stats.engagementRate}%` : "—"}
                  </Typography>
                </Stack>

                {/* Button */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaigns/${campaign.id}`);
                  }}
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    justifySelf: "end",
                  }}
                >
                  View Details
                </Button>
              </Box>
            </Box>
          );
        })}
    </Stack>
  );
}
